import { styled } from '@mui/system';
import { Box, Grid } from '@mui/material';
import {
  SectionHeading,
  SectionSubtext,
  StepCard,
  StepNumber,
  StepTitle,
  DescriptionText,
  CardTitle,
  ChecklistText,
  CollaborativeText,
  OutlinedButton,
  DisclaimerText,
} from '../../styles';
import { colors, fontFamilies } from '../../../../../constants/theme';

export const ExpectationsHeading = styled(SectionHeading)(({ theme }) => ({}));

export const ExpectationsSubtext = styled(SectionSubtext)(({ theme }) => ({
  marginTop: '14px',
  maxWidth: '950px',
  letterSpacing: '0.4px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    marginBottom: '30px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: '18px',
    marginBottom: '40px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '22px',
    marginBottom: '50px',
  },
}));

export const StepsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    gap: '16px',
  },
  [theme.breakpoints.up('sm')]: {
    gap: '20px',
  },
}));

export const ExpectationsStepCard = styled(StepCard)({
  width: '100%',
  maxWidth: '1150px',
});

export const StepContentBox = styled(Box)({});

export const StepContentWrapper = styled(Box)(({ theme }) => ({
  gap: '20px',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    margin: 'auto 16px',
    gap: '12px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    margin: 'auto 24px',
    gap: '16px',
  },
  [theme.breakpoints.up('md')]: {
    margin: 'auto 41px',
    gap: '20px',
  },
}));

export const ExpectationsStepNumber = styled(StepNumber)(({ theme }) => ({
  marginBottom: 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    textAlign: 'center',
  },
  [theme.breakpoints.between('sm', 'lg')]: {
    fontSize: '18px',
    textAlign: 'center',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '20px',
    textAlign: 'left',
  },
}));

export const ExpectationsStepTitle = styled(StepTitle)(({ theme }) => ({
  marginBottom: 0,
  fontWeight: 400,
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
    textAlign: 'center',
  },
  [theme.breakpoints.between('sm', 'lg')]: {
    fontSize: '28px',
    textAlign: 'center',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '32px',
    textAlign: 'left',
  },
}));

export const ExpectationsDescriptionText = styled(DescriptionText, {
  shouldForwardProp: (prop) => prop !== '$hasSubtitle',
})(({ theme, $hasSubtitle }) => ({
  color: colors.primary,
  marginBottom: $hasSubtitle ? '15px' : '0',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: '22px',
    textAlign: 'center',
  },
  [theme.breakpoints.between('sm', 'lg')]: {
    fontSize: '16px',
    lineHeight: '26px',
    textAlign: 'center',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '18px',
    lineHeight: '28px',
    textAlign: 'left',
  },
}));

export const ExpectationsCardTitle = styled(CardTitle)(({ theme }) => ({
  marginBottom: '15px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    textAlign: 'center',
  },
  [theme.breakpoints.between('sm', 'lg')]: {
    fontSize: '18px',
    textAlign: 'center',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '20px',
    textAlign: 'left',
  },
}));

export const ChecklistContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('lg')]: {
    alignItems: 'center',
  },
  [theme.breakpoints.up('lg')]: {
    alignItems: 'flex-start',
  },
}));

export const ExpectationsChecklistText = styled(ChecklistText)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: '16px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '18px',
  },
}));

export const ImageGridItem = styled(Grid)(({ theme }) => ({
  order: 2,
  marginTop: '16px',
  [theme.breakpoints.up('lg')]: {
    order: 0,
    marginTop: 0,
    flexShrink: 0,
  },
}));

export const ImageWrapper = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
});

export const StepImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  display: 'block',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    maxWidth: '500px',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '620px',
  },
}));

export const ClosingSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    marginTop: '24px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    marginTop: '32px',
  },
  [theme.breakpoints.up('md')]: {
    marginTop: '43px',
  },
}));

export const CollaborativeTextPrimary = styled(CollaborativeText)(({ theme }) => ({
  marginBottom: '10px',
  maxWidth: '950px',
  fontFamily: fontFamilies?.gotham,
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '30px',
  letterSpacing: '.4px',
  textAlign: 'center',
  textTransform: 'capitalize',

  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: '16px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
  },
}));

export const CollaborativeTextSecondary = styled(CollaborativeText)(({ theme }) => ({
  letterSpacing: '.5px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    marginBottom: '20px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: '18px',
    marginBottom: '25px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
    marginBottom: '30px',
  },
}));

export const ButtonsGrid = styled(Grid)(({ theme }) => ({
  maxWidth: '950px',
  margin: '0 auto 30px auto',
  [theme.breakpoints.down('sm')]: {
    gap: '12px',
    marginBottom: '20px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    gap: '16px',
    marginBottom: '25px',
  },
  [theme.breakpoints.up('md')]: {
    gap: '20px',
    marginBottom: '30px',
  },
}));

export const ExpectationsButton = styled(OutlinedButton)(({ theme }) => ({
  maxHeight: '60px',
  letterSpacing: 0,
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    padding: '15px 20px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: '18px',
    padding: '18px 25px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
    padding: '23px 30px',
  },
}));

export const ExpectationsDisclaimerText = styled(DisclaimerText)(({ theme }) => ({
  letterSpacing: '.4px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    lineHeight: '20px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    fontSize: '16px',
    lineHeight: '24px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
    lineHeight: '28px',
  },
}));
